import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Table, Space, notification, Popconfirm, Tag, Tooltip, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, AppstoreAddOutlined, SearchOutlined } from '@ant-design/icons';
import { CityService } from '../services/cityService';
import { City } from '../types/city';
import { DishService } from '../services/dishService';
import { Dish } from '../types/dish';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';

const { Search } = Input;

const CityManagement: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [dishAssignModalVisible, setDishAssignModalVisible] = useState(false);
  const [selectedDishes, setSelectedDishes] = useState<string[]>([]);
  const [loadingAssign, setLoadingAssign] = useState(false);

  const cityService = new CityService();
  const dishService = new DishService();

  const fetchCities = async () => {
    setLoading(true);
    try {
      const fetchedCities = await cityService.getAllCities();
      setCities(fetchedCities);
    } catch (error) {
      console.error('Error fetching cities:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách thành phố.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDishes = async () => {
    try {
      const fetchedDishes = await dishService.getAllDishes();
      setDishes(fetchedDishes);
    } catch (error) {
      console.error('Error fetching dishes:', error);
    }
  };

  useEffect(() => {
    fetchCities();
    fetchDishes();
  }, []);

  const handleAddOrUpdateCity = async (values: City) => {
    try {
      if (editingCity) {
        await cityService.updateCity(editingCity.id!, values);
        notification.success({
          message: 'Thành công',
          description: 'Thành phố đã được cập nhật thành công!',
        });
      } else {
        await cityService.addCity(values);
        notification.success({
          message: 'Thành công',
          description: 'Thành phố mới đã được thêm thành công!',
        });
      }
      setModalVisible(false);
      form.resetFields();
      fetchCities();
    } catch (error) {
      console.error('Error saving city:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể lưu thành phố.',
      });
    }
  };

  const handleEdit = (city: City) => {
    setEditingCity(city);
    form.setFieldsValue(city);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    // Kiểm tra xem có món ăn nào đang sử dụng thành phố này không
    const appliedDishes = dishes.filter(dish => dish.city === id);

    if (appliedDishes.length > 0) {
      notification.error({
        message: 'Không thể xóa',
        description: `Thành phố này đang được sử dụng bởi ${appliedDishes.length} món ăn. Vui lòng thay đổi thành phố của các món ăn trước khi xóa.`,
      });
      return;
    }

    try {
      await cityService.deleteCity(id);
      notification.success({
        message: 'Thành công',
        description: 'Thành phố đã được xóa thành công!',
      });
      fetchCities();
    } catch (error) {
      console.error('Error deleting city:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa thành phố.',
      });
    }
  };

  const handleAddNew = () => {
    setEditingCity(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true }); // Set default value for isActive
    setModalVisible(true);
  };

  const openDishAssignModal = (city: City) => {
    setEditingCity(city);
    const currentCityDishes = dishes.filter(dish => dish.city === city.id).map(dish => dish.id!);
    setSelectedDishes(currentCityDishes);
    setDishAssignModalVisible(true);
  };

  const handleAssignDishes = async () => {
    if (!editingCity) return;

    setLoadingAssign(true);
    try {
      // Lấy tất cả các món ăn đã thuộc thành phố này trước đó
      const previousCityDishes = dishes.filter(dish => dish.city === editingCity.id).map(dish => dish.id!);

      // Các món ăn cần thêm vào thành phố (các món mới được chọn)
      const dishesToAdd = selectedDishes.filter(id => !previousCityDishes.includes(id));

      // Các món ăn cần xóa khỏi thành phố (các món đã thuộc thành phố nhưng không được chọn nữa)
      const dishesToRemove = previousCityDishes.filter(id => !selectedDishes.includes(id));

      // Cập nhật các món ăn
      const updatePromises = [
        ...dishesToAdd.map(id => {
          const dish = dishes.find(d => d.id === id);
          if (dish) {
            return dishService.updateDish(id, { ...dish, city: editingCity.id });
          }
          return Promise.resolve();
        }),
        ...dishesToRemove.map(id => {
          const dish = dishes.find(d => d.id === id);
          if (dish) {
            return dishService.updateDish(id, { ...dish, city: '' });
          }
          return Promise.resolve();
        })
      ];

      await Promise.all(updatePromises);

      notification.success({
        message: 'Thành công',
        description: 'Đã cập nhật món ăn cho thành phố thành công!',
      });

      setDishAssignModalVisible(false);
      fetchDishes();
    } catch (error) {
      console.error('Error assigning dishes:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể cập nhật món ăn cho thành phố.',
      });
    } finally {
      setLoadingAssign(false);
    }
  };

  const columns = [
    {
      title: 'Tên thành phố',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a: City, b: City) => a.name.localeCompare(b.name),
    },
    {
      title: 'Mã thành phố',
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 300,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Đang hoạt động' : 'Ngừng hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Món ăn đang áp dụng',
      key: 'appliedDishes',
      width: 400,
      render: (_: any, record: City) => {
        const appliedDishes = dishes.filter(dish => dish.city === record.id);
        
        if (appliedDishes.length === 0) {
          return <span style={{ color: '#999' }}>Không có món ăn nào</span>;
        }

        const displayedDishes = appliedDishes.slice(0, 3);
        const remainingDishes = appliedDishes.slice(3);

        return (
          <Space wrap>
            {displayedDishes.map(dish => (
              <Tag color="blue" key={dish.id}>
                {dish.name}
              </Tag>
            ))}
            {remainingDishes.length > 0 && (
              <Tooltip
                title={
                  <div>
                    {remainingDishes.map(dish => (
                      <div key={dish.id}>{dish.name}</div>
                    ))}
                  </div>
                }
              >
                <Tag color="purple">+{remainingDishes.length} món khác</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 200,
      render: (_: any, record: City) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            icon={<AppstoreAddOutlined />}
            onClick={() => openDishAssignModal(record)}
          />
          <Popconfirm
            title="Xóa thành phố"
            description="Bạn có chắc chắn muốn xóa thành phố này?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredCities = searchTerm
    ? cities.filter(city =>
      city.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : cities;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>Quản lý thành phố</h1>
          <Tag color="blue">{cities.length} thành phố</Tag>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters">
            <Search
              placeholder="Tìm kiếm theo tên hoặc mã thành phố"
              allowClear
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
            />
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            className="admin-button"
          >
            Thêm thành phố mới
          </Button>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container">
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredCities}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1500 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Tổng số: ${total} thành phố`,
            }}
          />
        </div>
      </div>

      <Modal
        title={editingCity ? "Sửa thông tin thành phố" : "Thêm thành phố mới"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        className="admin-modal"
      >
        <div className="admin-form-container">
          <Form 
            form={form} 
            onFinish={handleAddOrUpdateCity} 
            layout="vertical"
          >
            <div className="admin-form-section">
              <h3 className="admin-form-section-title">Thông tin thành phố</h3>
              <Form.Item
                name="name"
                label="Tên thành phố"
                rules={[{ required: true, message: 'Vui lòng nhập tên thành phố!' }]}
              >
                <Input placeholder="Nhập tên thành phố" />
              </Form.Item>

              <Form.Item
                name="code"
                label="Mã thành phố"
                rules={[{ required: true, message: 'Vui lòng nhập mã thành phố!' }]}
              >
                <Input placeholder="Nhập mã thành phố" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Mô tả"
              >
                <Input.TextArea placeholder="Nhập mô tả" rows={4} />
              </Form.Item>

              <Form.Item
                name="isActive"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hoạt động" unCheckedChildren="Ngừng hoạt động" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="admin-button"
                >
                  {editingCity ? 'Cập nhật' : 'Thêm mới'}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        title={`Gán món ăn cho thành phố: ${editingCity?.name}`}
        open={dishAssignModalVisible}
        onCancel={() => setDishAssignModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDishAssignModalVisible(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingAssign}
            onClick={handleAssignDishes}
          >
            Lưu thay đổi
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <h4>Món ăn đã chọn ({selectedDishes.length}):</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedDishes.length > 0 ? (
              selectedDishes.map(id => {
                const dish = dishes.find(d => d.id === id);
                return dish ? (
                  <Tag
                    color="blue"
                    key={id}
                    closable
                    onClose={() => setSelectedDishes(prev => prev.filter(i => i !== id))}
                  >
                    {dish.name}
                  </Tag>
                ) : null;
              })
            ) : (
              <span style={{ color: '#999' }}>Chưa có món ăn nào được chọn</span>
            )}
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default CityManagement; 